import { FC, useEffect, useRef, useState } from "react";
import { useChatStore } from "../../../stores/ChatStore";
import { Message } from "../../../interfaces/Chat/Message";
import { FileAttachment, LocationAttachment, MediaAttachment, ReplyAttachment } from "../../../interfaces/Chat/MessageData";
import { fetchBlob, formatText, getFileType, isFile, isVideo, truncateText } from "../../../functions/general";
import { insertChatAttachment } from "../../../api/ResourceAPI";
import Swal from "sweetalert2";
import { ChatAttachmentPreview } from "./ChatAttachmentPreview";
import EmojiPicker from "emoji-picker-react";
import { QuickReply } from "../../../interfaces/Chat/QuickReply";
import { toAbsoluteUrl } from "../../../_metronic/helpers";
import { postReplyChatGroup, postSendChatGroup, putEditChat } from "../../../api/ChatCRUD";
import useAccessToken from "../../../hooks/AccessToken";
import { ChatGroupMessage } from "../../../interfaces/Chat/Chat";
import { chatMode, focusChatInput, replaceMentionsWithPhone, returnListMentions } from "../../../functions/chat";
import { useSettingsStore } from "../../../stores/SettingsStore";
import { returnMaps } from "../../General/ChatElement";
import { useIntl } from "react-intl";
import { MentionsInput, Mention, OnChangeHandlerFunc, SuggestionDataItem, MentionsInputProps } from 'react-mentions'
import useHandleCheckSessionChat from "../../../hooks/HandleCheckSessionChat";

const mentionStyle: MentionsInputProps["style"] = {
    control: {
        backgroundColor: '#fff',
        fontSize: "1.1rem",
        minHeight: "40px",
        maxHeight: "200px",
        overflowY: "auto",
    },

    '&multiLine': {
        control: {
            minHeight: 60,
        },
        highlighter: {
            padding: 9,
        },
        input: {
            padding: 10,
            border: "none",
            outline: "none",
        },
    },

    '&singleLine': {
        display: 'inline-block',
        width: 180,

        highlighter: {
            padding: 1,
        },
        input: {
            padding: 1,
        },
    },

    suggestions: {
        list: {
            backgroundColor: 'white',
            border: '1px solid rgba(0,0,0,0.15)',
            fontSize: 16,
            bottom: "100%",
            position: "absolute",
            left: 0,
            right: 0,
            width: "200px",
        },
        item: {
            padding: '5px 15px',
            borderBottom: '1px solid rgba(0,0,0,0.15)',
            '&focused': {
                backgroundColor: '#f3f4f6',
                borderRadius: "8px",
            },
        },
    },
};

const ChatGroupInput: FC = () => {
    const intl = useIntl()
    const { settings } = useSettingsStore()
    const token = useAccessToken()
    const { handleCheckSession } = useHandleCheckSessionChat();
    const { groupMessage, setGroupMessage,
        imageBlobUrls, setImageBlobUrls, videoBlobUrls,
        setVideoBlobUrls, fileBlobUrls, setFileBlobUrls,
        groupID, editMessage, listChatGroupRooms,
        replyGroupMessage, setReplyGroupMessage, selectedChatPhone,
        tokenChat, quickReplies, listChatGroupParticipants
    } = useChatStore()

    const participants = listChatGroupRooms[groupID]?.participants.map((participant) => {
        const key = participant.phone
        return {
            name: listChatGroupParticipants[key]?.name ? listChatGroupParticipants[key].name : participant.name,
            phone: listChatGroupParticipants[key]?.phone ? listChatGroupParticipants[key].phone : participant.phone,
            color: listChatGroupParticipants[key]?.color ? listChatGroupParticipants[key].color : participant.color,
            lid: listChatGroupParticipants[key]?.lid ? listChatGroupParticipants[key].lid : participant.lid
        }
    }) ?? []

    const [isSendMessagse, seIsSendMessage] = useState<boolean>(false)
    const [isUploadingAttachment, setIsUploadingAttachment] = useState<boolean>(false)
    const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false)
    const [isQuickReplyVisible, setIsQuickReplyVisible] = useState<boolean>(false)
    const [filteredQuickReplies, setFilteredQuickReplies] = useState<QuickReply[]>(quickReplies);
    const listParticipants: SuggestionDataItem[] = participants.filter((participant) => selectedChatPhone[0].phone !== participant.phone).map((participant) => ({ id: participant.phone, display: participant.name }))
    const [idChatGroup, setIdChatGroup] = useState('');
    const [chatAs, setChatAs] = useState('');
    const emojiPickerRef = useRef<HTMLDivElement>(null);
    const colorMessage = replyGroupMessage ? listChatGroupParticipants[replyGroupMessage.waSender.phone].color : ''

    const handleReplyChat = async (as_phone: string, messageId: string, reply_message: string, attachment?: MediaAttachment | FileAttachment) => {
        seIsSendMessage(true)

        let attachmentTemp: ReplyAttachment | undefined
        if (attachment) {
            if (attachment.type === 'image' || attachment.type === 'video') {
                attachmentTemp = {
                    type: attachment.type,
                    urlni: attachment.url,
                }
            } else {
                attachmentTemp = {
                    type: attachment.type,
                    urlni: attachment.url,
                    filename: (attachment as FileAttachment).name,
                    mimetype: (attachment as FileAttachment).mimetype,
                }
            }
        }

        const data = {
            groupId: idChatGroup,
            repliedMessageId: messageId,
            message: {
                text: replaceMentionsWithPhone(reply_message),
                attachment: attachmentTemp,
                mentions: returnListMentions(reply_message),
            },
            as: as_phone,
            mode: chatMode(settings.chat_version)
        }

        try {
            const response = await postReplyChatGroup(data.groupId, data.repliedMessageId, data.message, data.as, data.mode, token, tokenChat);
            if (response.data.success) {
                setReplyGroupMessage(undefined)
                setGroupMessage('')
            }
        } catch (error: any) {
            const funct = () => handleReplyChat(as_phone, messageId, reply_message, attachment);
            handleCheckSession('replyChatGroup', funct);
        } finally {
            seIsSendMessage(false)
        }
    }

    const handleEditChat = async (receiver_phone: string, as_phone: string, messageId: string, new_message: string) => {
        try {
            const response = await putEditChat(messageId, receiver_phone, as_phone, new_message, chatMode(settings.chat_version), token, tokenChat);
            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Pesan berhasil diubah',
                })
            }
        } catch (error: any) {
            const funct = () => handleEditChat(receiver_phone, as_phone, messageId, new_message);
            handleCheckSession('editChatGroup', funct);
        }
    }

    const handleSendMessage = async (as_phone: string, message: string, attachment?: MediaAttachment | FileAttachment | LocationAttachment) => {
        seIsSendMessage(true)
        const text = replaceMentionsWithPhone(message)
        const mentions = returnListMentions(message)

        try {
            let response;
            if (attachment) response = await postSendChatGroup(idChatGroup, as_phone, text, mentions, chatMode(settings.chat_version), token, tokenChat, attachment);
            else response = await postSendChatGroup(idChatGroup, as_phone, text, mentions, chatMode(settings.chat_version), token, tokenChat);
            if (response.data.success) {
                setGroupMessage('')
            }
        } catch (error: any) {
            const funct = () => handleSendMessage(as_phone, message, attachment);
            handleCheckSession('sendChatGroup', funct);
        } finally {
            seIsSendMessage(false)
        }
    }

    const sendMessage = async (attachment?: MediaAttachment | FileAttachment, attachmentMessage?: string) => {
        setIsQuickReplyVisible(false)

        if (replyGroupMessage) {
            handleReplyChat(chatAs, replyGroupMessage.id!, groupMessage, attachment)
        }

        if (groupMessage.trim() === '' && !attachment) return;

        if ((groupMessage || attachment) && !isUploadingAttachment && !replyGroupMessage) {
            handleSendMessage(chatAs, attachmentMessage ?? groupMessage, attachment)
        }
    }

    const uploadAttachment = async () => {
        if (!isUploadingAttachment) {
            var listUrlAttachment: any[] = []
            setIsUploadingAttachment(true)

            if (imageBlobUrls[groupID] && imageBlobUrls[groupID].blobs.length > 0) {
                try {
                    setImageBlobUrls({
                        ...imageBlobUrls,
                        [groupID]: {
                            ...imageBlobUrls[groupID],
                            isUploading: true,
                        },
                    });

                    await Promise.all(imageBlobUrls[groupID].blobs.map(async (imageUrl, index) => {
                        try {
                            // Fetch the image blob and filename
                            const { blob, fileName } = await fetchBlob(imageUrl);

                            // Convert the Blob to a File
                            const file = new File([blob], fileName, { type: blob.type });

                            // Now you can use the 'file' object for your operations
                            const resourceResponse = await insertChatAttachment(file);

                            if (resourceResponse.data.success) {
                                // remove the image from the array
                            }

                            const newMediaAttachment: MediaAttachment = {
                                type: 'image',
                                url: resourceResponse.data.data[0].url,
                                filename: resourceResponse.data.data[0].filename,
                            }
                            listUrlAttachment.push(newMediaAttachment)
                        } catch (error) {
                            console.error('Error uploading image:', imageUrl, error);
                        } finally {
                            setIsUploadingAttachment(false)
                        }
                    }));
                } catch (error) {
                    console.log(error);
                }
            }

            if (videoBlobUrls[groupID] && videoBlobUrls[groupID].blobs.length > 0) {
                try {
                    setVideoBlobUrls({
                        ...videoBlobUrls,
                        [groupID]: {
                            ...videoBlobUrls[groupID],
                            isUploading: true,
                        },
                    });

                    await Promise.all(videoBlobUrls[groupID].blobs.map(async (videoUrl, index) => {
                        try {
                            // Fetch the video blob and filename
                            const { blob, fileName } = await fetchBlob(videoUrl);

                            // Convert the Blob to a File
                            const file = new File([blob], fileName, { type: blob.type });

                            // Now you can use the 'file' object for your operations
                            const resourceResponse = await insertChatAttachment(file);

                            if (resourceResponse.data.success) {
                                // remove the image from the array
                            }

                            const newMediaAttachment: MediaAttachment = {
                                type: 'video',
                                url: resourceResponse.data.data[0].url,
                                filename: resourceResponse.data.data[0].filename,
                            }
                            listUrlAttachment.push(newMediaAttachment)
                        } catch (error) {
                            console.error('Error uploading video:', videoUrl, error);
                        } finally {
                            setIsUploadingAttachment(false)
                        }
                    }));
                } catch (error) {
                    console.log(error);
                }
            }

            if (fileBlobUrls[groupID] && fileBlobUrls[groupID].blobs.length > 0) {
                try {
                    setFileBlobUrls({
                        ...fileBlobUrls,
                        [groupID]: {
                            ...fileBlobUrls[groupID],
                            isUploading: true,
                        },
                    });

                    await Promise.all(fileBlobUrls[groupID].blobs.map(async (fileBlob, index) => {
                        try {
                            // Fetch the file blob and filename
                            const { blob } = await fetchBlob(fileBlob.blob);

                            // Convert the Blob to a File
                            const file = new File([blob], fileBlob.name, { type: blob.type });

                            // Now you can use the 'file' object for your operations
                            const resourceResponse = await insertChatAttachment(file);

                            if (resourceResponse.data.success) {
                                // remove the image from the array
                            }

                            const newFileAttachment: FileAttachment = {
                                type: 'file',
                                url: resourceResponse.data.data[0].url,
                                name: fileBlob.name,
                                mimetype: resourceResponse.data.data[0].filemimetype,
                            }
                            listUrlAttachment.push(newFileAttachment)
                        } catch (error) {
                            console.error('Error uploading file:', fileBlob.name, error);
                        } finally {
                            setIsUploadingAttachment(false)
                        }
                    }));
                } catch (error) {
                    console.log(error);
                }
            }

            if (listUrlAttachment.length > 0) {
                await Promise.all(listUrlAttachment.map((attachment, index) => {
                    sendMessage(attachment, listUrlAttachment.length === index + 1 ? groupMessage : '')
                }))

                handleClearAttachment()
            } else {
                sendMessage()
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, editedMessage?: Message) => {
        if (e.key === '/') {
            setIsQuickReplyVisible(true)
        } else if (isQuickReplyVisible && e.key === " ") {
            setIsQuickReplyVisible(false)
        } else {
            setIsQuickReplyVisible(false)
        }

        // Check if Ctrl key is pressed and the key is 'b'
        if ((e.ctrlKey && e.key.toLowerCase() === 'b') || (e.metaKey && e.key.toLowerCase() === 'b')) {
            // Prevent the default behavior of the browser
            e.preventDefault();

            // Get the selected text
            const selectedText = window.getSelection()?.toString();

            // If there is selected text, replace the selected text with '*'
            if (selectedText) {
                const updatedMessage = groupMessage.replace(selectedText, `*${selectedText}*`);
                setGroupMessage(updatedMessage);
            } else {
                // If no text is selected, insert '*' at the current cursor position
                const cursorPosition = e.currentTarget.selectionStart || 0;
                const newMessage = groupMessage.slice(0, cursorPosition) + '**' + groupMessage.slice(cursorPosition);
                setGroupMessage(newMessage);

                // Adjust the cursor position after inserting '*'
                e.currentTarget.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
            }
        } else if ((e.ctrlKey && e.key.toLowerCase() === 'i') || (e.metaKey && e.key.toLowerCase() === 'i')) {
            // Prevent the default behavior of the browser
            e.preventDefault();

            // Get the selected text
            const selectedText = window.getSelection()?.toString();

            // If there is selected text, replace the selected text with '_'
            if (selectedText) {
                const updatedMessage = groupMessage.replace(selectedText, `_${selectedText}_`);
                setGroupMessage(updatedMessage);
            } else {
                // If no text is selected, insert '_' at the current cursor position
                const cursorPosition = e.currentTarget.selectionStart || 0;
                const newMessage = groupMessage.slice(0, cursorPosition) + '__' + groupMessage.slice(cursorPosition);
                setGroupMessage(newMessage);

                // Adjust the cursor position after inserting '_*'
                e.currentTarget.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
            }
        }

        // Call your existing onKeyDown logic if needed
        if (editedMessage) {
            onEnterPress(e, editedMessage);
        } else {
            onEnterPress(e);
        }
    };

    const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>, editedMessage?: Message) => {
        if (e.keyCode === 13 && e.shiftKey === false) {
            e.preventDefault()
            if ((
                (imageBlobUrls[groupID] && imageBlobUrls[groupID].blobs.length > 0) ||
                (videoBlobUrls[groupID] && videoBlobUrls[groupID].blobs.length > 0) ||
                (fileBlobUrls[groupID] && fileBlobUrls[groupID].blobs.length > 0)) &&
                !isUploadingAttachment && !isSendMessagse
            ) {
                uploadAttachment()
            } else if (!isUploadingAttachment && !editedMessage && !replyGroupMessage && !isSendMessagse) {
                sendMessage()
            } else if (editedMessage) {
                Swal.fire({
                    title: 'Mohon tunggu',
                    html: 'Sedang mengedit pesan',
                    didOpen: () => {
                        Swal.showLoading()
                    },
                })
                handleEditChat(idChatGroup, chatAs, editedMessage.id!, editMessage)
            } else if (replyGroupMessage) {
                handleReplyChat(chatAs, replyGroupMessage.id!, groupMessage)
            }
        }
    }

    const handleInputChange: OnChangeHandlerFunc = (value, plainTextValue, mentions) => {
        const inputText = plainTextValue;
        setGroupMessage(inputText);

        // --- QUICK REPLIES ---
        const lastSlashIndex = inputText.lastIndexOf("/");
        if (lastSlashIndex !== -1) {
            const shortcut = inputText
                .slice(lastSlashIndex + 1)
                .split(" ")[0]
                .trim()
                .toLowerCase();

            const matches = quickReplies.filter((reply) =>
                reply.shortcut.toLowerCase().startsWith(shortcut)
            );

            setFilteredQuickReplies(matches);
            setIsQuickReplyVisible(matches.length > 0);
        } else {
            setIsQuickReplyVisible(false);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;

        if (files && files.length > 0) {
            // Create an array to store blob URLs
            const newImageBlobUrls: string[] = [];
            const newVideoBlobUrls: string[] = [];
            const newFileBlobUrls: { name: string, blob: string }[] = [];

            // Loop through each file
            for (const file of files) {
                // Check if the file is an image (you can modify this check based on your criteria)
                if (file.type.startsWith('image/')) {
                    const blobUrl = URL.createObjectURL(file);
                    newImageBlobUrls.push(blobUrl);
                } else if (file.type.startsWith('video/')) {
                    const blobUrl = URL.createObjectURL(file);
                    newVideoBlobUrls.push(blobUrl);
                } else {
                    const blobUrl = URL.createObjectURL(file);
                    const name = file.name;
                    newFileBlobUrls.push({ name, blob: blobUrl });
                }
            }

            handleSetAttachment(newImageBlobUrls, newVideoBlobUrls, newFileBlobUrls);
        }
    };

    const handleClearAttachment = () => {
        setImageBlobUrls({
            ...imageBlobUrls,
            [groupID]: {
                blobs: [],
                isUploading: false,
            },
        });

        setVideoBlobUrls({
            ...videoBlobUrls,
            [groupID]: {
                blobs: [],
                isUploading: false,
            },
        });

        setFileBlobUrls({
            ...fileBlobUrls,
            [groupID]: {
                blobs: [],
                isUploading: false,
            },
        });
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const items = e.clipboardData?.items;

        if (items) {
            const newImageBlobUrls: string[] = [];
            const newVideoBlobUrls: string[] = [];
            const newFileBlobUrls: { name: string, blob: string }[] = [];

            for (const item of items) {
                if (item.type.indexOf('image') !== -1) {
                    const blob = item.getAsFile();

                    if (blob) {
                        const blobUrl = URL.createObjectURL(blob);
                        newImageBlobUrls.push(blobUrl);
                    }
                } else if (item.type.indexOf('video') !== -1) {
                    const blob = item.getAsFile();

                    if (blob) {
                        const blobUrl = URL.createObjectURL(blob);
                        newVideoBlobUrls.push(blobUrl);
                    }
                } else {
                    const blob = item.getAsFile();

                    if (blob) {
                        const blobUrl = URL.createObjectURL(blob);
                        const name = blob.name;
                        newFileBlobUrls.push({ name, blob: blobUrl });
                    }
                }
            }

            if (newImageBlobUrls.length > 0 || newVideoBlobUrls.length > 0) {
                e.preventDefault()
            }

            handleSetAttachment(newImageBlobUrls, newVideoBlobUrls, newFileBlobUrls);
        }
    };

    const handleAttachmentQuickReply = (dataQuickReply: QuickReply) => {
        if (dataQuickReply.attachments) {
            const newImageBlobUrls: string[] = [];
            const newVideoBlobUrls: string[] = [];
            const newFileBlobUrls: { name: string, blob: string }[] = [];

            dataQuickReply.attachments.map((attachment) => {
                if (attachment.type === 'image') {
                    newImageBlobUrls.push(attachment.url);
                } else if (attachment.type === 'video') {
                    newVideoBlobUrls.push(attachment.url);
                } else {
                    newFileBlobUrls.push({ name: attachment.name, blob: attachment.url });
                }
            })

            handleSetAttachment(newImageBlobUrls, newVideoBlobUrls, newFileBlobUrls);
        }
    }

    const handleSetAttachment = (newImageBlobUrls: string[], newVideoBlobUrls: string[], newFileBlobUrls: { name: string, blob: string }[]) => {
        if (!imageBlobUrls[groupID]) {
            setImageBlobUrls({
                ...imageBlobUrls,
                [groupID]: {
                    isUploading: false,  // Add this line if needed
                    blobs: newImageBlobUrls,
                },
            });
        } else {
            setImageBlobUrls({
                ...imageBlobUrls,
                [groupID]: {
                    ...imageBlobUrls[groupID],
                    blobs: [...imageBlobUrls[groupID].blobs, ...newImageBlobUrls],
                },
            });
        }

        if (!videoBlobUrls[groupID]) {
            setVideoBlobUrls({
                ...videoBlobUrls,
                [groupID]: {
                    isUploading: false,  // Add this line if needed
                    blobs: newVideoBlobUrls,
                },
            });
        } else {
            setVideoBlobUrls({
                ...videoBlobUrls,
                [groupID]: {
                    ...videoBlobUrls[groupID],
                    blobs: [...videoBlobUrls[groupID].blobs, ...newVideoBlobUrls],
                },
            });
        }

        if (!fileBlobUrls[groupID]) {
            setFileBlobUrls({
                ...fileBlobUrls,
                [groupID]: {
                    isUploading: false,  // Add this line if needed
                    blobs: newFileBlobUrls,
                },
            });
        } else {
            setFileBlobUrls({
                ...fileBlobUrls,
                [groupID]: {
                    ...fileBlobUrls[groupID],
                    blobs: [...fileBlobUrls[groupID].blobs, ...newFileBlobUrls],
                },
            });
        }
    }

    const onEmojiClick = (emojiObject: any) => {
        const { groupMessage } = useChatStore.getState();
        setGroupMessage(groupMessage + emojiObject.emoji);
    };

    const handleCancelReplyChat = () => {
        setReplyGroupMessage(undefined)
        handleClearAttachment()
    }

    useEffect(() => {
        if (groupID) {
            let dataIdChatGroup = groupID.split('|')[0]
            let dataChatAs = groupID.split('|')[1]
            setIdChatGroup(dataIdChatGroup)
            setChatAs(dataChatAs)
            handleCancelReplyChat()
        }
    }, [groupID])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
                setShowEmojiPicker(false);
            }
        };

        if (showEmojiPicker) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showEmojiPicker]);

    return (
        <>
            {
                (imageBlobUrls[groupID] || videoBlobUrls[groupID] || fileBlobUrls[groupID]) &&
                <div className='mb-5 overflow-auto d-flex' style={{ whiteSpace: 'nowrap' }}>
                    {
                        imageBlobUrls[groupID].blobs.map((blobUrl, index) => (
                            <ChatAttachmentPreview key={index} index={index} blobUrl={blobUrl} type='image' />
                        ))}
                    {
                        videoBlobUrls[groupID].blobs.map((blobUrl, index) => (
                            <ChatAttachmentPreview key={index} index={index} blobUrl={blobUrl} type='video' />
                        ))
                    }
                    {
                        fileBlobUrls[groupID].blobs.map((blob, index) => (
                            <ChatAttachmentPreview index={index} blobUrl={blob.blob} type='file' />
                        ))
                    }
                </div>
            }

            {
                replyGroupMessage &&
                <div className="chat-reply bg-secondary p-3 rounded d-flex justify-content-between">
                    <div id='reply-content' style={{ width: "100%" }}>
                        <p id='reply-header' className='fw-bolder'>
                            <p style={{ color: colorMessage }}>
                                {
                                    replyGroupMessage.fromMe ? 'You' : (replyGroupMessage as ChatGroupMessage).waSender.name
                                }
                            </p>
                            {
                                replyGroupMessage.attachmentName &&
                                <>
                                    {
                                        (replyGroupMessage.attachmentName.endsWith('.jpg') || replyGroupMessage.attachmentName.endsWith('.png') || replyGroupMessage.attachmentName.endsWith('.jpeg')) ?
                                            <span>
                                                <i className="fas fa-image me-1" />
                                                {replyGroupMessage.attachmentName}
                                            </span>
                                            :
                                            (replyGroupMessage.attachmentName!.endsWith('.mp4') || replyGroupMessage.attachmentName!.endsWith('.mov')) ?
                                                <span>
                                                    <i className="fas fa-video me-1" />
                                                    {replyGroupMessage.attachmentName}
                                                </span>
                                                :
                                                replyGroupMessage.attachmentName &&
                                                <span>
                                                    <i className="fas fa-file me-1" />
                                                    {replyGroupMessage.attachmentName}
                                                </span>
                                    }
                                </>
                            }
                            {
                                replyGroupMessage.message.includes('[User sends location]') &&
                                returnMaps(replyGroupMessage, listChatGroupParticipants, listChatGroupRooms[groupID], intl, "chatReply")
                            }
                        </p>
                        {
                            replyGroupMessage.message && !replyGroupMessage.message.includes('[User sends location]') &&
                            <span id='reply-text'>
                                <div dangerouslySetInnerHTML={{ __html: formatText(replyGroupMessage.message, listChatGroupParticipants, listChatGroupRooms[groupID], false, true) }} className='fw-normal' />
                            </span>
                        }
                    </div>
                    <span role="button" onClick={handleCancelReplyChat}>
                        <i className="fas fa-times-circle fa-lg m-2 text-danger"></i>
                    </span>
                </div>
            }

            {isQuickReplyVisible && (
                <div className="quick-reply-dropdown">
                    {filteredQuickReplies.map((reply, index) => (
                        <div
                            key={index}
                            className="quick-reply-option"
                            onClick={() => {
                                // Find the last index of "/"
                                const lastSlashIndex = groupMessage.lastIndexOf("/");

                                const [, ...newText] = groupMessage.slice(lastSlashIndex + 1, groupMessage.length).split(" ")
                                const newMessage = groupMessage.slice(0, lastSlashIndex) + reply.message + " " + newText;

                                // Set the updated message
                                handleAttachmentQuickReply(reply);
                                setGroupMessage(newMessage);
                                setIsQuickReplyVisible(false);
                                focusChatInput()
                            }}
                        >
                            <div className="d-flex gap-2">
                                <span className="fw-bolder">{reply.shortcut} -</span>
                                {
                                    reply.attachments &&
                                    reply.attachments.map((attachment, index) => (
                                        isFile(attachment?.url) ?
                                            <div className="position-relative">
                                                {
                                                    getFileType(attachment?.url!) === "pdf" ? <img src={toAbsoluteUrl('/media/logos/pdf.png')} className="mb-2" alt={attachment?.name!} style={{ width: '20px', height: '20px', objectFit: 'cover' }} />
                                                        : getFileType(attachment?.url!) === "doc" || getFileType(attachment?.url!) === "docx" ? <img src={toAbsoluteUrl('/media/logos/doc.png')} className="mb-2" alt={attachment?.name!} style={{ width: '20px', height: '20px', objectFit: 'cover' }} />
                                                            : getFileType(attachment?.url!) === "ppt" || getFileType(attachment?.url!) === "pptx" ? <img src={toAbsoluteUrl('/media/logos/ppt.png')} className="mb-2" alt={attachment?.name!} style={{ width: '20px', height: '20px', objectFit: 'cover' }} />
                                                                : getFileType(attachment?.url!) === "xlsx" || getFileType(attachment?.url!) === "xls" ? <img src={toAbsoluteUrl('/media/logos/xls.png')} className="mb-2" alt={attachment?.name!} style={{ width: '20px', height: '20px', objectFit: 'cover' }} />
                                                                    : <img src={toAbsoluteUrl('/media/logos/file.png')} className="mb-2" alt={attachment?.name!} style={{ width: '20px', height: '20px', objectFit: 'cover' }} />
                                                }
                                            </div>
                                            :
                                            isVideo(attachment?.url) ?
                                                <div className="position-relative">
                                                    <img src={toAbsoluteUrl('/media/logos/video.png')} className="mb-2" alt={attachment?.name!} style={{ width: '20px', height: '20px', objectFit: 'cover' }} />
                                                </div>
                                                :
                                                <div className="position-relative">
                                                    <img src={attachment?.url} className="rounded mb-2" alt={attachment?.name!} style={{ width: '20px', height: '20px', objectFit: 'cover' }} />
                                                </div>
                                    ))
                                }
                                <span>{truncateText(reply.message, 100)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <MentionsInput
                placeholder={intl.formatMessage({ id: 'FORM.LABEL.TYPE_A_MESSAGE' })}
                style={mentionStyle}
                value={groupMessage}
                allowSpaceInQuery={true}
                onChange={handleInputChange}
                onKeyDown={(e) => handleKeyDown(e as React.KeyboardEvent<HTMLTextAreaElement>)}
                onPaste={handlePaste}
            >
                <Mention
                    trigger="@"
                    data={listParticipants}
                    displayTransform={(id, display) => `@${display} `}
                    style={{ fontWeight: "500", color: "#21c063", position: "relative", zIndex: 1, backgroundColor: 'white' }}
                />
            </MentionsInput>

            <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center me-2'>
                    <div>
                        <label htmlFor="fileInput" className="btn btn-sm btn-icon btn-active-light-primary me-1">
                            <i className='bi bi-paperclip fs-1'></i>
                        </label>
                        <input
                            type="file"
                            id="fileInput"
                            multiple
                            style={{ display: 'none' }}
                            onChange={(event) => {
                                handleFileInputChange(event); // Panggil handler Anda
                                event.target.value = ""; // Reset nilai input untuk memungkinkan memilih file yang sama
                            }}
                        // accept only image and video files
                        />
                    </div>
                    <label className="btn btn-sm btn-icon btn-active-light-primary me-1" onClick={() => {
                        setShowEmojiPicker(!showEmojiPicker)
                    }}>
                        <i className="fas fa-smile fs-1" />
                    </label>
                    {showEmojiPicker && (
                        <div ref={emojiPickerRef} className="position-absolute" style={{ bottom: "20%", zIndex: 2 }}>
                            <EmojiPicker onEmojiClick={onEmojiClick} emojiVersion={'5.0'} />
                        </div>
                    )}
                </div>
                <button
                    className='btn btn-success'
                    style={{ backgroundColor: "#50cd89", color: "white" }}
                    type='button'
                    data-kt-element='send'
                    onClick={() => {
                        if (
                            (imageBlobUrls[groupID] && imageBlobUrls[groupID].blobs.length > 0) ||
                            (videoBlobUrls[groupID] && videoBlobUrls[groupID].blobs.length > 0) ||
                            (fileBlobUrls[groupID] && fileBlobUrls[groupID].blobs.length > 0)
                        ) {
                            uploadAttachment()
                        } else {
                            sendMessage()
                        }
                    }}
                    disabled={isUploadingAttachment || isSendMessagse}
                >
                    {intl.formatMessage({ id: 'FORM.ACTION.SEND' }, { title: '' })}
                </button>
            </div>
        </>
    )
}

export { ChatGroupInput }